export enum CollectionChangingAction {
    Add = 1,
    Remove,
    Sort
}

export type OnCollectionChangedCallback<TItem = any> = (args: CollectionChangedEventArgs<TItem>) => void;

export interface CollectionChangedEventArgs<TItem = any> extends Readonly<InternalCollectionChangedEventArgs<TItem>> { }

interface InternalCollectionChangedEventArgs<TItem = any> {
    sender: Object;
    action: CollectionChangingAction;
    newItems: TItem[];
    oldItems: TItem[];
}

/**
 * Mutable operations:
 * - insert,
 * - push,
 * - pop,
 * - clear,
 * - remove,
 * - removeAt,
 * - shift,
 * - sort,
 * - unshift.
 */
export class ObservableCollection<TItem> implements ArrayLike<TItem> {
    private readonly items: TItem[];

    protected readonly collectionChangedCallbacks: OnCollectionChangedCallback<any>[];

    public constructor(items?: TItem[]) {
        this.items = items ? items.slice() : [];
        this.collectionChangedCallbacks = [];

        this.defineRangeOfIndexProperties(0, this.items.length - 1);
    }

    readonly [n: number]: TItem;

    public get length(): number {
        return this.items.length;
    }

    public asArray(): TItem[] {
        return this.items.slice();
    }

    public filter(callbackfn: (value: TItem, index: number, collection: ObservableCollection<TItem>) => boolean): TItem[] {
        return this.items.filter((v, i) => callbackfn(v, i, this));
    }

    public find(predicate: (value: TItem, index: number, collection: ObservableCollection<TItem>) => boolean): TItem | undefined {
        return this.items.find((v, i) => predicate(v, i, this));
    }

    public forEach(callbackfn: (value: TItem, index: number, collection: ObservableCollection<TItem>) => void): void {
        this.items.forEach((v, i) => callbackfn(v, i, this));
    }

    public indexOf(searchElement: TItem, fromIndex?: number): number {
        return this.items.indexOf(searchElement, fromIndex);
    }

    public insert(index: number, item: TItem): number {
        this.items.splice(index, 0, item);
        this.defineIndexPropertyIfNotExist(this.items.length - 1);

        this.notifyCollectionChanged({
            action: CollectionChangingAction.Add,
            newItems: [item]
        });

        return this.items.length;
    }

    public map<U>(callbackfn: (value: TItem, index: number, collection: TItem[]) => U, thisArg?: any): U[] {
        return this.items.map(callbackfn, thisArg);
    }

    public push(...items: TItem[]): number {
        const index = this.items.length;
        const length = this.items.push(...items);

        this.defineRangeOfIndexProperties(index, length - 1);

        items.length > 0 && this.notifyCollectionChanged({
            action: CollectionChangingAction.Add,
            newItems: items,
        });

        return length;
    }

    public pop(): TItem | undefined {
        const popped = this.items.pop();

        this.notifyCollectionChanged({
            action: CollectionChangingAction.Remove,
            oldItems: popped !== undefined ? [popped] : []
        });

        return popped;
    }

    public clear(): void {
        this.items.length > 0 && this.remove(...this.items);
    }

    public remove(...items: TItem[]): void {
        const deleted: TItem[] = [];

        items.forEach(item => {
            const index = this.items.indexOf(item);
            index > -1 && deleted.push(...this.items.splice(index, 1));
        });

        deleted.length > 0 && this.notifyCollectionChanged({
            action: CollectionChangingAction.Remove,
            oldItems: deleted
        });
    }

    public removeAt(index: number): TItem {
        if (index >= this.items.length || index < 0)
            throw new RangeError(`Index out of bounds.`);

        const deleted = this.items.splice(index, 1);

        this.notifyCollectionChanged({
            action: CollectionChangingAction.Remove,
            oldItems: deleted
        });

        return deleted[0];
    }

    public reduce(callbackfn: (previousValue: TItem, currentValue: TItem, currentIndex: number, collection: ObservableCollection<TItem>) => TItem, initialValue?: TItem): TItem;
    public reduce<U>(callbackfn: (previousValue: U, currentValue: TItem, currentIndex: number, collection: ObservableCollection<TItem>) => U, initialValue: U): U;
    public reduce(callbackfn: any, initialValue?: any) {
        return this.items.reduce((prev, curr, i) => callbackfn(prev, curr, i, this), initialValue);
    }

    public reduceRight(callbackfn: (previousValue: TItem, currentValue: TItem, currentIndex: number, collection: ObservableCollection<TItem>) => TItem, initialValue?: TItem): TItem;
    public reduceRight<U>(callbackfn: (previousValue: U, currentValue: TItem, currentIndex: number, collection: ObservableCollection<TItem>) => U, initialValue: U): U;
    public reduceRight(callbackfn: any, initialValue?: any) {
        return this.items.reduceRight((prev, curr, i) => callbackfn(prev, curr, i, this), initialValue);
    }

    public shift(): TItem | undefined {
        const shifted = this.items.shift();

        this.notifyCollectionChanged({
            action: CollectionChangingAction.Remove,
            oldItems: shifted !== undefined ? [shifted] : []
        });

        return shifted;
    }

    public slice(start?: number, end?: number): ObservableCollection<TItem> {
        const subset = this.items.slice(start, end);
        return new ObservableCollection(subset);
    }

    public some(callbackfn: (value: TItem, index: number, collection: ObservableCollection<TItem>) => boolean): boolean {
        return this.items.some((v, i) => callbackfn(v, i, this));
    }

    public sort(compareFn?: (a: TItem, b: TItem) => number): this {
        this.items.sort(compareFn);

        this.notifyCollectionChanged({
            action: CollectionChangingAction.Sort
        });

        return this;
    }

    public unshift(...items: TItem[]): number {
        const length = this.items.unshift(...items);

        this.defineRangeOfIndexProperties(length - items.length, length - 1);

        this.notifyCollectionChanged({
            action: CollectionChangingAction.Add,
            newItems: items
        });

        return length;
    }

    public onCollectionChanged(callbackFn: OnCollectionChangedCallback<TItem>): Function {
        this.collectionChangedCallbacks.push(callbackFn);
        return () => this.collectionChangedCallbacks.splice(this.collectionChangedCallbacks.indexOf(callbackFn), 1);
    }

    private notifyCollectionChanged(args: Partial<InternalCollectionChangedEventArgs<TItem>>): void {
        const e = <CollectionChangedEventArgs<TItem>> {
            sender: this,
            newItems: [],
            oldItems: [],
            ...args
        };

        if ((e.newItems.length === 0 && e.oldItems.length === 0) && e.action !== CollectionChangingAction.Sort)
            return;

        this.collectionChangedCallbacks.forEach(callback => callback(e));
    }

    private defineIndexPropertyIfNotExist(index: number): void {
        if (index in this)
            return;

        Object.defineProperty(this, index, {
            configurable: true,
            enumerable: true,
            get: (() => this.items[index]).bind(this)
        });
    }

    private defineRangeOfIndexProperties(start: number, end: number): void {
        while (start <= end) {
            this.defineIndexPropertyIfNotExist(start++);
        }
    }
}

export function isObservableCollection<TData = unknown>(value: any): value is ObservableCollection<TData> {
    return value instanceof ObservableCollection;
}
